<template>
  <div>
    <sd-section-title :title="t('cmp_title')" :note="t('cmp_note')" style="margin-top:26px" />

    <!-- picker panel -->
    <div class="sd-card cmp-panel">
      <div class="cmp-row">
        <span class="ylab">{{ t('cmp_dim') }}</span>
        <v-btn-toggle :value="st.d" mandatory dense @change="setDim">
          <v-btn v-for="d in DIMS" :key="d" small text :value="d">{{ t('cmp_d_' + d) }}</v-btn>
        </v-btn-toggle>
        <span class="grow" />
        <span class="sd-hint">{{ t('cmp_presets') }}</span>
        <v-chip v-for="p in presets" :key="p.k" small outlined class="sd-btn" @click="preset(p.k)">{{ t(p.l) }}</v-chip>
      </div>

      <!-- surveys picker -->
      <template v-if="st.d === 'surveys'">
        <div class="cmpsel">
          <v-chip v-for="(k, i) in st.i" :key="k" close @click:close="removeItem(k)"><span class="cdot" :style="{background: COLORS[i % COLORS.length]}" />{{ label(k) }}</v-chip>
          <span v-if="!st.i.length" class="sd-hint">{{ t('cmp_need') }}</span>
        </div>
        <template v-if="!surveysFull">
          <v-text-field v-model="q" class="sd-search" dense outlined hide-details prepend-inner-icon="mdi-magnify" :placeholder="t('cmp_pick_srv')" />
          <div class="cmplist">
            <button v-for="r in surveyList" :key="r.id" @click="addSurvey(r.id)">
              <span class="svbadge" :class="SEC_CLS[r.sectorEn] || 'b3'">{{ isAr ? r.sectorAr : r.sectorEn }}</span>
              <span class="nm">{{ surveyName(r, lang) }}</span>
              <span v-if="hasLoc(r)" class="locbadge">📍 <span class="num-ltr">{{ locPct(r) }}%</span></span>
              <span class="mt">{{ compact(r.participants) }} · {{ r.satPct != null ? pctS(r.satPct) : '—' }}</span>
            </button>
            <div v-if="!surveyList.length" class="sd-hint" style="padding:10px">{{ t('no_data') }}</div>
          </div>
        </template>
        <span v-else class="sd-hint">{{ st.c.length ? t('cmp_combo_max', { n: COMBO_MAX }) : t('cmp_max', { n: MAX.surveys }) }}</span>
        <div class="cmp-row">
          <span class="ylab">{{ t('cmp_cities_opt') }}</span>
          <v-chip small :color="st.c.length ? '' : 'primary'" :dark="!st.c.length" @click="setCities([])">{{ t('cmp_nosplit') }}</v-chip>
          <v-chip-group :value="st.c" multiple column @change="setCities">
            <v-chip v-for="c in cities" :key="c" small filter :value="c">{{ cityName(c, lang) }}</v-chip>
          </v-chip-group>
        </div>
        <span class="sd-hint">{{ t('cmp_combo_max', { n: COMBO_MAX }) }} · {{ t('cmp_city_note') }}<template v-if="st.c.length"> · {{ t('cmp_loc_only', { n: locatedSurveys.length }) }}</template></span>
      </template>

      <!-- years / cities subject picker -->
      <template v-else-if="st.d === 'years' || st.d === 'cities'">
        <div class="cmp-row">
          <span class="ylab">{{ t('cmp_subject') }}</span>
          <v-select :value="st.s" :items="subjectItems" dense outlined hide-details style="max-width:420px" @change="setSubject" />
          <span v-if="st.d === 'cities'" class="sd-hint">{{ t('cmp_loc_only', { n: locatedSurveys.length }) }}</span>
        </div>
        <div class="cmp-row">
          <span class="ylab">{{ st.d === 'years' ? t('cmp_pick_years') : t('cmp_sel') }}</span>
          <v-chip-group :value="st.i" multiple column @change="setItems">
            <v-chip v-for="k in (st.d === 'years' ? subjectYears : cities)" :key="k" small filter :value="String(k)">{{ st.d === 'years' ? k : cityName(k, lang) }}</v-chip>
          </v-chip-group>
          <span class="sd-hint">{{ t('cmp_max', { n: MAX[st.d] }) }}<template v-if="st.d === 'cities'"> · {{ t('cmp_city_note') }}</template></span>
        </div>
      </template>

      <!-- sectors / regions -->
      <template v-else>
        <div class="cmp-row">
          <span class="ylab">{{ t('cmp_sel') }}</span>
          <v-chip-group :value="st.i" multiple column @change="setItems">
            <v-chip v-for="o in (st.d === 'sectors' ? sectorOpts : regionOpts)" :key="o.k" small filter :value="o.k">{{ o.l }}</v-chip>
          </v-chip-group>
          <span class="sd-hint">{{ t('cmp_max', { n: MAX[st.d] }) }}</span>
        </div>
      </template>

      <!-- shared year filter -->
      <div v-if="st.d !== 'years'" class="cmp-row">
        <span class="ylab">{{ t('cmp_year') }}</span>
        <v-btn-toggle :value="st.y == null ? 0 : years.indexOf(st.y) + 1" mandatory dense @change="setYear">
          <v-btn small text>{{ t('yr_all') }}</v-btn>
          <v-btn v-for="y in years" :key="y" small text>{{ y }}</v-btn>
        </v-btn-toggle>
        <span class="sd-hint">{{ t('cmp_year_h') }}</span>
      </div>
    </div>

    <!-- body -->
    <div v-if="st.i.length < 2" class="sd-card cmp-empty">{{ t('cmp_need') }}</div>
    <div v-else-if="loading" class="dload"><v-progress-circular indeterminate color="primary" size="22" width="3" style="margin-inline-end:10px" />{{ t('cmp_loading') }}</div>
    <div v-else-if="items.length < 2" class="sd-card cmp-empty">{{ t('cmp_need') }}</div>
    <template v-else>
      <div v-if="an.noReg" class="sd-card sd-warnbox">⚠️ {{ t('cmp_noreg') }}</div>
      <div class="callout" :style="{borderInlineStartColor: an.gapC}">
        <span class="co-badge">{{ t('cmp_verdict') }}</span>
        <p v-html="an.verdict" />
        <div v-if="an.gap != null" class="cmpv"><div class="gapbig num-ltr" :style="{color: an.gapC}">{{ an.gap }}</div><div class="gapcap">{{ t('cmp_gap_cap') }}</div></div>
        <span class="sd-hint">{{ t('cmp_scope') }}: <span class="num-ltr" v-html="an.scope" /></span>
      </div>
      <section class="sd-grid" style="margin-top:18px">
        <div class="sd-card c12">
          <div class="head">
<div><h3>{{ t('cmp_table') }}</h3><p class="hint">{{ t('cmp_table_h') }}</p></div>
            <v-btn color="primary" depressed small class="sd-btn" @click="csv"><v-icon small left>mdi-download</v-icon>{{ t('cmp_csv') }}</v-btn>
</div>
          <div style="overflow:auto">
            <table class="cmpt">
              <thead>
<tr>
<th />
                <th v-for="it in items" :key="it.key">
<span class="cdot" :style="{background: it.color}" />
                  <a v-if="it.surveyId" @click.prevent="$emit('navigate', { name: 'survey', id: it.surveyId })">{{ it.name }}</a><template v-else>{{ it.name }}</template>
                  <span v-if="it.small" class="cmpsmall">{{ t('cmp_small') }}</span><div v-if="it.sub" class="sub">{{ it.sub }}</div>
</th>
                <th>{{ t('cmp_diff') }}</th>
</tr>
</thead>
              <tbody>
<tr v-for="r in an.rows" :key="r.label">
<td class="lab">{{ r.label }}</td>
                <td v-for="(c, i) in r.cells" :key="i" class="v" :class="{best: c.best, worst: c.worst}">
                  <span v-if="c.text == null" style="color:var(--sd-ink-4)">—</span><span v-else :class="{'num-ltr': c.raw}">{{ c.text }}</span>
                  <span v-if="c.best" class="bw">{{ t('cmp_best') }}</span><span v-else-if="c.worst" class="bw">{{ t('cmp_worst') }}</span>
</td>
                <td class="gap">{{ r.gap }}</td>
</tr>
</tbody>
            </table>
          </div>
        </div>
        <div class="sd-card c6">
          <div class="head"><div><h3>{{ t('cmp_dist') }}</h3><p class="hint">{{ t('cmp_dist_h') }}</p></div></div>
          <div v-for="it in items" :key="it.key" class="cmpdist">
            <div class="nm">
<span class="cdot" :style="{background: it.color, margin: 0}" />{{ it.name }}
              <b v-if="it.rated" :style="{color: satColorPct(it.satPct)}">{{ it.satPct != null ? pctS(it.satPct) : '—' }}</b>
              <b v-else style="color:var(--sd-ink-4);font-weight:500">{{ t(it.empty || (it.sub && !it.parts) ? 'cmp_nocity' : 'cmp_no_sat') }}</b>
</div>
            <sd-sat-bar v-if="it.rated" :buckets="it.B" small />
          </div>
          <div class="sd-legend"><span v-for="[k, b] in BUCKETS" :key="b" class="lg"><span class="sw" :style="{background: BUCKET_C[b]}" />{{ t(k) }}</span></div>
        </div>
        <div class="sd-card c6">
          <div class="head"><div><h3>{{ t('cmp_ins') }}</h3><p class="hint">{{ t('cmp_ins_h') }}</p></div><span class="ai-badge">✦ {{ t('ai_b') }}</span></div>
          <sd-insight-list :items="an.ins" />
        </div>
        <div v-if="an.trendYears.length >= 2" class="sd-card c6">
          <div class="head"><div><h3>{{ t('cmp_trend') }}</h3><p class="hint">{{ t('cmp_trend_h') }}</p></div></div>
          <sd-line-chart :series="an.trendSeries" :years="an.trendYears" :hl="an.hl" :labels="an.trendSeries.length === 1" />
          <div class="sd-legend"><span v-for="s in an.trendSeries" :key="s.name" class="lg"><span class="sw" :style="{background: s.color}" />{{ s.name }}</span></div>
        </div>
        <div v-if="an.partYears.length && an.partSeries.length" class="sd-card c6">
          <div class="head"><div><h3>{{ t('cmp_part') }}</h3><p class="hint">{{ t('cmp_part_h') }}</p></div></div>
          <sd-bars-chart :series="an.partSeries" :years="an.partYears" :hl="an.hl" />
        </div>
        <div v-if="an.shared.length" class="sd-card c12">
          <div class="head">
<div><h3>{{ t('cmp_qs') }}</h3><p class="hint">{{ t('cmp_qs_h', { n: an.shared.length }) }}</p></div>
            <div class="cmplegend"><span v-for="it in items" :key="it.key"><span class="cdot" :style="{background: it.color}" />{{ it.name }}</span></div>
</div>
          <div v-for="(qd, qi) in (showAllQ ? an.shared : an.shared.slice(0, 6))" :key="qi" class="cqcard">
            <div class="qh"><span class="qnum">{{ qi + 1 }}</span><span class="qt">{{ qd.t }}</span><span class="qtype tnum">{{ qd.tots.map(fmt).join(' · ') }}</span></div>
            <div v-for="(o, oi) in qd.opts" :key="oi" class="cqopt">
              <span class="ot" :title="o.t">{{ o.t }}</span>
              <div class="cqb"><div v-for="(v, i) in o.vals" :key="i" class="cqbar"><span class="trk"><i :style="{width: (v || 0) + '%', background: items[i].color}" /></span><span class="num-ltr">{{ v == null ? '—' : v + '%' }}</span></div></div>
            </div>
          </div>
          <div v-if="an.shared.length > 6" style="text-align:center;margin-top:14px"><v-btn text small color="primary" class="sd-btn" @click="showAllQ = !showAllQ">{{ showAllQ ? t('cmp_qs_less') : t('cmp_qs_more', { n: an.shared.length - 6 }) }}</v-btn></div>
        </div>
      </section>
    </template>
  </div>
</template>
<script>
import { sdMixin } from '../store.js'
import { fmt, compact, pctS, surveyName, satColorPct, SEC_CLS, BUCKETS, BUCKET_C } from '../lib/format.js'
import { matrixYears, cityList, cityName, regionName, REGIONS } from '../lib/stats.js'
import * as C from '../lib/compare.js'
import { exportTable } from '../lib/exportCsv.js'
import { SERIES_COLORS } from '../theme.js'
import SdSectionTitle from '../components/SectionTitle.vue'
import SdSatBar from '../components/charts/SatBar.vue'
import SdLineChart from '../components/charts/LineChart.vue'
import SdBarsChart from '../components/charts/BarsChart.vue'
import SdInsightList from '../components/InsightList.vue'
export default {
  name: 'SdCompareView',
  components: { SdSectionTitle, SdSatBar, SdLineChart, SdBarsChart, SdInsightList },
  mixins: [sdMixin],
  /** `query` = { d, i, s, y, c } (strings, as in a URL). The view emits `update:query` whenever the selection changes. */
  props: { query: { type: Object, default: () => ({}) } },
  data() { const D = (this.$sd && this.$sd.state.data) || {} /* computed props are not ready inside data() */
    return { st: C.stateFromQuery(D, this.query), q: '', loading: false, loadedKey: '', showAllQ: false, DIMS: C.DIMS, MAX: C.MAX, COMBO_MAX: C.COMBO_MAX, COLORS: SERIES_COLORS, SEC_CLS, BUCKETS, BUCKET_C,
    presets: [{ k: 'sec', l: 'cmp_p_sec' }, { k: 'yr', l: 'cmp_p_yr' }, { k: 'reg', l: 'cmp_p_reg' }, { k: 'srv', l: 'cmp_p_srv' }, { k: 'city', l: 'cmp_p_city' }] } },
  computed: {
    ctx() { return { D: this.D, lang: this.lang, t: this.t, details: this.$sd.detailsMap() } },
    years() { return matrixYears(this.D) },
    cities() { return cityList(this.D) },
    lb() { return this.D.leaderboard || [] },
    locatedSurveys() { return this.lb.filter(C.hasLoc) },
    surveysFull() { return this.st.i.length >= C.MAX.surveys || (this.st.c.length && (this.st.i.length + 1) * this.st.c.length > C.COMBO_MAX) },
    surveyList() {
      const q = this.q.trim().toLowerCase()
      return this.lb.filter((r) => !this.st.i.includes(String(r.id)) && (!this.st.c.length || C.hasLoc(r)) && (!q || ((r.nameAr || '') + ' ' + (r.nameEn || '')).toLowerCase().includes(q)))
        .sort((a, b) => (this.st.c.length ? (b.located - a.located) : 0) || (b.participants - a.participants)).slice(0, 40)
    },
    subjectItems() {
      const list = this.st.d === 'cities' ? this.locatedSurveys.slice().sort((a, b) => b.located - a.located) : this.lb.slice().sort((a, b) => b.participants - a.participants)
      return [{ value: 'all', text: this.t('cmp_all_platform') }, ...list.map((r) => ({ value: String(r.id), text: surveyName(r, this.lang) + (this.st.d === 'cities' ? ` (📍 ${C.locPct(r)}%)` : '') }))]
    },
    subjectYears() { const r = this.st.s === 'all' ? null : C.lbById(this.D, this.st.s); return r ? C.yearsOf(r) : this.years },
    sectorOpts() { return C.sectorList(this.D).map((en) => ({ k: en, l: C.sectorName(this.D, en, this.lang) })) },
    regionOpts() { return REGIONS.map((r, i) => ({ k: String(i), l: regionName(r.ar, this.lang) })) },
    needed() { return C.neededIds(this.D, this.st) },
    items() { if (this.loading) return []; return C.buildItems(this.ctx, this.st) },
    an() { return C.analyze(this.ctx, this.st, this.items) },
  },
  watch: {
    query: { deep: true, handler(q) { const next = C.stateFromQuery(this.D, q); if (JSON.stringify(next) !== JSON.stringify(this.st)) { this.st = next; this.ensure() } } },
  },
  created() { this.ensure() },
  methods: {
    fmt, compact, pctS, surveyName, satColorPct, cityName, hasLoc: C.hasLoc, locPct: C.locPct,
    label(k) { return C.subjectLabel(this.D, this.st, k, this.lang) },
    commit() { this.showAllQ = false; this.$emit('update:query', C.queryFromState(this.st)); this.ensure() },
    async ensure() {
      const ids = this.needed.filter((id) => !this.$sd.state.details[id] && !this.$sd.state.detailMissing[id])
      if (!ids.length) { this.loading = false; return }
      this.loading = true; const key = ids.join(',')
      await this.$sd.surveys(ids)
      if (key === ids.join(',')) this.loading = false
    },
    setDim(d) { if (d && d !== this.st.d) { this.st = C.freshState(this.D, d); this.q = ''; this.commit() } },
    preset(k) {
      if (k === 'city') { const top = this.locatedSurveys.slice().sort((a, b) => b.located - a.located)[0]; if (!top) return; this.st = { d: 'cities', i: this.cities.slice(0, 3), s: String(top.id), y: null, c: [] } }
      else this.st = C.freshState(this.D, k === 'sec' ? 'sectors' : k === 'yr' ? 'years' : k === 'reg' ? 'regions' : 'surveys')
      this.commit()
    },
    setYear(idx) { this.st.y = idx > 0 ? this.years[idx - 1] : null; this.commit() },
    setItems(v) { let i = (v || []).map(String); if (i.length > C.MAX[this.st.d]) i = i.slice(0, C.MAX[this.st.d]); if (this.st.d === 'years') i.sort((a, b) => a - b); this.st.i = i; this.commit() },
    setSubject(s) { this.st.s = s; if (this.st.d === 'years') { const r = s === 'all' ? null : C.lbById(this.D, s); const ys = r ? C.yearsOf(r) : this.years; this.st.i = ys.slice(-2).map(String) } this.commit() },
    setCities(v) {
      let c = (v || []).filter(Boolean).slice(0, C.MAX.cities); this.st.c = c
      if (c.length) this.st.i = this.st.i.filter((id) => C.hasLoc(C.lbById(this.D, id)))
      while (c.length && this.st.i.length * c.length > C.COMBO_MAX) this.st.i.pop()
      this.commit()
    },
    addSurvey(id) { if (this.surveysFull) return; this.st.i.push(String(id)); this.q = ''; this.commit() },
    removeItem(k) { this.st.i = this.st.i.filter((x) => x !== k); this.commit() },
    csv() {
      const head = ['Metric', ...this.items.map((it) => it.name), this.t('cmp_diff')]
      exportTable('comparison-' + this.st.d + '.csv', head, this.an.rows.map((r) => [r.label, ...r.cells.map((c) => (c.v == null ? '' : c.v)), r.gap]))
    },
  },
}
</script>
